// Copyright (c) AdCritter. All rights reserved.
import BillboardLocations from 'pages/MasterCampaignPlanPage/new/components/Projections/BillboardLocations'
import DeliveryMetrics from 'pages/MasterCampaignPlanPage/new/components/Projections/DeliveryMetrics'
import PlacementCharts from 'pages/MasterCampaignPlanPage/new/components/Projections/PlacementCharts'
import TargetedHouseholdLocations from 'pages/MasterCampaignPlanPage/new/components/Projections/TargetedHouseholdLocations'
import TabPageHeader from 'pages/MasterCampaignPlanPage/new/components/TabPageHeader'
import { FC } from 'react'

import { EuiLoadingContent, EuiSpacer } from '@elastic/eui'

import { useGetBillboardsByZipCodesQuery } from 'api/billboardApi'
import { useGetDomainsQuery } from 'api/domainsApi'
import { AIPlanPreview } from 'api/entities/AIPlanPreview'
import { MasterCampaignPlanCampaignBuilderType, MasterCampaignStub } from 'api/entities/MasterCampaignStub'
import { useHouseholdCensusData } from 'utils/useHouseholdCensusData'
import { useBillboardZipCodes, useHouseholdZipCodes } from 'utils/useProjectionsUtils'

interface AIPlanProjectionsProps {
  aiPlan: AIPlanPreview
}

const AIPlanProjections: FC<AIPlanProjectionsProps> = ({ aiPlan }) => {
  const masterCampaignStub = (aiPlan.aiPlanStub ?? { name: '', campaignBuilders: [] }) as unknown as MasterCampaignStub
  const billboardZipCodes = useBillboardZipCodes(masterCampaignStub)
  const householdZipCodes = useHouseholdZipCodes(masterCampaignStub)
  const section = aiPlan.projectionsSection

  const { isFetching: isBillboardDataFetching } = useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  const { isWaiting: isCensusDataWaiting, hasCensusData } = useHouseholdCensusData(householdZipCodes)
  const { isFetching: isDomainsFetching } = useGetDomainsQuery()

  const isProjectionsDataLoading = isBillboardDataFetching || isCensusDataWaiting || isDomainsFetching

  const pageTitle = section?.pageTitle || 'Projections'
  const hasInternet = masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Internet)
  const hasTv = masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.TV)

  const masterCampaignPlanShim = {
    tvNetworkDistribution: aiPlan.tvNetworkDistribution,
    internetMarginOfError: aiPlan.internetMarginOfError,
    tvMarginOfError: aiPlan.tvMarginOfError,
    billboardMarginOfError: aiPlan.billboardMarginOfError,
    highlightedDomainIds: aiPlan.highlightedDomainIds,
    projectionsSection: section
  } as any

  if (isProjectionsDataLoading) {
    return (
      <>
        <TabPageHeader title={pageTitle} />
        <EuiLoadingContent lines={10} />
      </>
    )
  }

  return (
    <>
      <TabPageHeader title={pageTitle} />

      <DeliveryMetrics masterCampaignStub={masterCampaignStub} masterCampaignPlan={masterCampaignPlanShim} section={section?.cpmAndImpressions as any} />

      {(hasInternet || hasTv) && (
        <>
          <EuiSpacer size='l' />

          <PlacementCharts masterCampaignPlan={masterCampaignPlanShim} hasInternet={hasInternet} hasTv={hasTv} whereAdsAppearTitle={section?.whereAdsAppearTitle} auctionBasedPlacementsSection={section?.auctionBasedPlacements as any} ctvSection={section?.ctvPlacementCategories as any} internetSection={section?.internetAdPlacements as any} />

          {hasCensusData && (
            <>
              <EuiSpacer size='l' />

              <TargetedHouseholdLocations masterCampaignStub={masterCampaignStub} section={section?.geographicDistribution as any} />
            </>
          )}
        </>
      )}

      {masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard) && (
        <>
          <EuiSpacer size='l' />

          <BillboardLocations masterCampaignStub={masterCampaignStub} section={section?.billboardLocations as any} />
        </>
      )}
    </>
  )
}

export default AIPlanProjections
